import { TITLE } from '@/typings/enums/TITLE';
import { LANGUAGE_LEVEL } from '@/typings/enums/LANGUAGE_LEVEL';
import { reactive, ref, watch } from 'vue';
import BaseEntity from '@/entity/BaseEntity';
import { MOBILITY } from '@/typings/enums/MOBILITY';
import UserProExpEntity from '../UserProExpEntity';
import UserProSkillEntity from '../UserProSkillEntity';
import UserLanguageEntity from '../UserLanguageEntity';
import { useNotif } from '@orion.ui/orion';
import useLanguageService from '@/services/LanguageService';
import { pickFrom } from '@/utils/tools';
import useUserService from '@/services/user/UserService';
import useTempWorkerInformationService from '@/services/temp-worker/TempWorkerInformationService';
import useGouvService from '@/services/GouvService';

export default class UserProfileInformationsEntity extends BaseEntity<IUserFullInformationForAppModel> {

	protected citiesOptions = ref<string[]>([]);

	private readonly privateState = reactive({
		professionalExperiences: [] as UserProExpEntity[],
		professionalSkills: [] as UserProSkillEntity[],
		userLanguages: [] as UserLanguageEntity[],
	});


	get email () {return this.state.email; }
	get authLogin () { return this.self.authLogin; }
	get birthDay () { return this.state.birthDay; }
	get tempWorkerInformation () { return this.state.tempWorkerInformation; }
	get mobilePhone () { return this.state.mobilePhone; }
	get homePhone () { return this.state.homePhone; }

	get firstName () { return this.state.firstName; }
	set firstName (val) { this.state.firstName = val; }

	get lastName () { return this.state.lastName; }
	set lastName (val) { this.state.lastName = val; }

	get title () { return this.state.title; }
	set title (val) { this.state.title = val; }

	get readableBirthDay () { return this.birthDay ? new Date(this.birthDay) : null; }
	set readableBirthDay (val) { this.state.birthDay = val?.toPost(true); }

	get birthCity () { return this.state.birthCity; }
	set birthCity (val) { this.state.birthCity = val; }

	get birthCountry () { return this.state.birthCountry; }
	set birthCountry (val) { this.state.birthCountry = val; }

	get birthDepartment () { return this.state.birthDepartment; }
	set birthDepartment (val) { this.state.birthDepartment = val; }

	get nationality () { return this.state.nationality; }
	set nationality (val) { this.state.nationality = val; }

	get mobilePhoneCountry () { return this.state.mobilePhoneCountry; }
	set mobilePhoneCountry (val) { this.state.mobilePhoneCountry = val; }

	get homePhoneCountry () { return this.state.homePhoneCountry; }
	set homePhoneCountry (val) { this.state.homePhoneCountry = val; }

	get address () { return this.state.address; }
	set address (val) { this.state.address = val; }

	get additionalAddress () { return this.state.additionalAddress; }
	set additionalAddress (val) { this.state.additionalAddress = val; }

	get city () { return this.state.city; }
	set city (val) { this.state.city = val; }

	get country () { return this.state.country; }
	set country (val) { this.state.country = val; }

	readonly titlesOptions = [
		{
			label: 'Madame',
			value: TITLE.Madam,
		},
		{
			label: 'Monsieur',
			value: TITLE.Mister,
		},
	];

	readonly mobilityOptions = [
		{
			label: 'Moins de 10km',
			value: MOBILITY.Lte10Km,
		},
		{
			label: 'Moins de 30km',
			value: MOBILITY.Lte30Km,
		},
		{
			label: 'Moins de 50km',
			value: MOBILITY.Lte50Km,
		},
		{
			label: 'Plus de 50km',
			value: MOBILITY.Gte50Km,
		},
	];

	get mobilePhoneForInput () {
		return {
			phoneNumber: this.state.mobilePhone,
			phoneCountryCode: this.state.mobilePhoneCountry,
		} as Orion.Phone;
	}

	set mobilePhoneForInput (val) {
		this.state.mobilePhone = val.phoneNumber?.length ? val.phoneNumber : undefined;
		this.state.mobilePhoneCountry = val.phoneCountryCode?.length ? val.phoneCountryCode : undefined;
	}

	get homePhoneForInput () {
		return {
			phoneNumber: this.state.homePhone,
			phoneCountryCode: this.state.homePhoneCountry,
		} as Orion.Phone;
	}

	set homePhoneForInput (val) {
		this.state.homePhone = val.phoneNumber?.length ? val.phoneNumber : undefined;
		this.state.homePhoneCountry = val.phoneCountryCode?.length ? val.phoneCountryCode : undefined;
	}


	get postCode () { return this.state.postCode; }
	set postCode (val) {
		this.state.postCode = val;
		this.getCitiesAsync();
	}


	get introduction () { return this.state.tempWorkerInformation?.introduction ?? undefined; }
	set introduction (val) {
		this.state.tempWorkerInformation
			? this.state.tempWorkerInformation.introduction = val
			: undefined;
	}

	get professionalSkills () { return this.privateState.professionalSkills; }

	get professionalExperiences () { return this.privateState.professionalExperiences as UserProExpEntity[]; };

	get userLanguages () { return this.privateState.userLanguages; }

	get hasPersonalVehicle () { return this.state.tempWorkerInformation?.hasPersonalVehicle ?? false; }
	set hasPersonalVehicle (val) {
		if (this.state.tempWorkerInformation) this.state.tempWorkerInformation.hasPersonalVehicle = val;
		this.updateHasPersonalVehicleAsync();
	}

	get mobility () { if (this.state.tempWorkerInformation) return this.state.tempWorkerInformation.mobility; }
	set mobility (val) {
		if (val === this.state.tempWorkerInformation?.mobility) return;
		if (this.state.tempWorkerInformation && (val === 0 || val)) {
			this.state.tempWorkerInformation.mobility = val;
			this.updateMobilityAsync();
		}
	}

	get dataForApiUpdate (): IUpdateUserAndPersonnalInformationModel {
		return {
			id: this.state.id,
			firstName: this.firstName,
			lastName: this.lastName,
			title: this.title,
			birthDay: this.birthDay,
			birthCity: this.birthCity,
			birthCountry: this.birthCountry,
			birthDepartment: this.birthDepartment,
			nationality: this.nationality,
			mobilePhoneCountry: this.mobilePhoneCountry,
			mobilePhone: this.state.mobilePhone,
			homePhone: this.state.homePhone,
			homePhoneCountry: this.homePhoneCountry,
			address: this.address,
			additionalAddress: this.additionalAddress,
			postCode: this.postCode,
			city: this.city,
			country: this.country,
		};
	}

	get dataForMobilityApiUpdate (): IUpdateMobilityTempWorkerInformationModel {
		return {
			id: this.state.tempWorkerInformation?.id ?? 0,
			mobility: this.mobility ?? MOBILITY.Lte10Km,
		};
	}

	get dataForHasPersonalVehicleApiUpdate (): IUpdateHasPersonalVehicleTempWorkerInformationModel {
		return {
			id: this.state.tempWorkerInformation?.id ?? 0,
			hasPersonalVehicle: this.hasPersonalVehicle,
		};
	}

	get dataForInformationApiUpdate (): IUpdateIntroductionTempWorkerInformationModel {
		return {
			id: this.state.tempWorkerInformation?.id ?? 0,
			introduction: this.introduction,
		};
	}

	get publicState () {
		return pickFrom(
			this,
			'id',
			'firstName',
			'lastName',
			'email',
			'title',
			'birthDay',
			'birthCity',
			'birthCountry',
			'birthDepartment',
			'nationality',
			'mobilePhoneCountry',
			'mobilePhone',
			'homePhone',
			'homePhoneCountry',
			'address',
			'additionalAddress',
			'city',
			'country',
			'tempWorkerInformation',
			'professionalExperiences',
			'userLanguages',
			'professionalSkills',
		);
	}


	constructor (payload?: IUserFullInformationForAppModel) {
		super(payload);

		watch(() => this.state.professionalExperiences?.length, () => {
			this.privateState.professionalExperiences = UserProExpEntity.map(
				this.state.professionalExperiences ?? [],
				userExpProEntity => userExpProEntity.connectUserProfileInformationsEntity(this),
			);
		});

		watch(() => this.state.userLanguages?.length, () => {
			this.privateState.userLanguages = UserLanguageEntity.map(
				this.state.userLanguages ?? [],
				userLangEntity => userLangEntity.connectUserProfileInformationsEntity(this),
			);
		});

		watch(() => this.state.professionalSkills?.length, () => {
			this.privateState.professionalSkills = UserProSkillEntity.map(
				this.state.professionalSkills ?? [],
				UserSkillProEntity => UserSkillProEntity.connectUserProfileInformationsEntity(this),
			);
		});
	}


	async getCitiesAsync () {
		if (this.state.postCode) {
			const data = await useGouvService().getListCityAsync(this.state.postCode);

			if (data.length) {
				this.citiesOptions.value = data.mapKey('nom');
				this.state.city = data[0].nom;
			} else {
				this.citiesOptions.value = [];
				this.state.city = undefined;
			}
		}
	}

	getReadableLanguageLevel (level: LANGUAGE_LEVEL) {
		return useLanguageService().getReadableLanguageLevel(level);
	}

	maskDepartement () {
		return {
			display: (val: string) => {
				if (!val) return;

				// Corse
				if (val.charAt(0) === '2' && /^2(\d|a|b)?$/.test(val)) {
					return val;
				}

				val = val.replace(/\D/g, '');

				if (val.charAt(0) === '0' && val.length === 3) { // Only one leading zero
					return val.slice(1);
				} else if (val.slice(0, 2) === '97') { // DOM
					return val.slice(0, 3);
				}
				return val.slice(0, 2);
			},
			value: () => undefined,
		};
	}

	async saveAsync () {
		await useUserService().updateAsync(this.dataForApiUpdate);
		await this.self.refreshAsync();
	}

	addProSkill (skill: IProfessionalSkillModel) {
		this.state.professionalSkills ? this.state.professionalSkills.push(skill) : this.state.professionalSkills = [skill];
	}

	deleteProSkill (code: string) {
		this.state.professionalSkills.deleteWhere('code', code);
	}

	addProExp (proExp: IProfessionalExperienceModel) {
		this.state.professionalExperiences.push(proExp);
	}

	deleteProExp (id: number) {
		this.state.professionalExperiences.deleteWhere('id', id);
	}

	addLanguage (language: IUserLanguageModel) {
		this.state.userLanguages.push(language);
	}

	deleteLanguage (id: number) {
		this.state.userLanguages.deleteWhere('id', id);
	}

	async updateHasPersonalVehicleAsync () {
		try {
			await useTempWorkerInformationService().updateHasPersonalVehicleAsync(this.dataForHasPersonalVehicleApiUpdate);
			useNotif.success('Sauvegarde réussie', 'Votre mobilité à bien été sauvegardée');
		} catch (e: any) {
			useNotif.danger(`Oops`, e?.response?.data);
		}
	}

	async updateMobilityAsync () {
		try {
			await useTempWorkerInformationService().updateMobilityAsync(this.dataForMobilityApiUpdate);
			await this.self.refreshAsync();
			useNotif.success('Sauvegarde réussie', 'Votre mobilité à bien été sauvegardée');
		} catch (e: any) {
			useNotif.danger(`Oops`, e?.response?.data);
		}
	}

	async updateIntroductionAsync () {
		try {
			await useTempWorkerInformationService().updateIntroductionAsync(this.dataForInformationApiUpdate);
		} catch (e: any) {
			useNotif.danger(`Oops`, e?.response?.data);
		}
	}
}

